import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flame, ArrowRight, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.role === "admin") {
        toast.success(`Bentornato, coach ${u.name}!`);
        navigate("/admin");
      } else {
        toast.success("Bentornato, atleta!");
        navigate("/");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di login");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) {
      toast.error("Inserisci prima la tua email");
      return;
    }
    setForgotLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      if (r.data.email_sent) {
        toast.success("Email di reset inviata. Controlla la casella!");
      } else if (r.data.email_configured) {
        toast.info("Se l'email è registrata, riceverai il link a breve");
      } else {
        toast.info("Contatta il tuo coach per generare un link di reset");
      }
    } catch {
      toast.error("Errore invio email");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14898419/pexels-photo-14898419.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0F] via-[#0B0B0F]/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#FF3B30]" strokeWidth={2.5} />
            <span className="font-display font-black text-2xl uppercase">Fit<span className="text-[#FF3B30]">Command</span></span>
          </div>
          <div>
            <div className="font-display text-6xl xl:text-7xl font-black uppercase leading-[0.9] tracking-tight max-w-lg">
              L'unità di misura<br/>è il <span className="text-[#FF3B30]">sudore.</span>
            </div>
            <div className="mt-6 max-w-md text-[#A0A0AB]">
              Home workout intelligenti con manubri, elastici, kettlebell e corpo libero.
              Costruiti su di te dal tuo coach IA.
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Flame className="w-6 h-6 text-[#FF3B30]" strokeWidth={2.5} />
            <span className="font-display font-black text-2xl uppercase">Fit<span className="text-[#FF3B30]">Command</span></span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Accesso Atleta</div>
          <h1 className="font-display text-5xl font-black uppercase leading-none tracking-tight mb-8">
            Bentornato<span className="text-[#FF3B30]">.</span>
          </h1>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-[#A0A0AB] mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-[#141419] border border-[#2A2A35] px-4 py-3 focus:border-[#FF3B30] focus:outline-none transition-colors"
                placeholder="tua@email.it"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-[#A0A0AB] mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full bg-[#141419] border border-[#2A2A35] px-4 py-3 focus:border-[#FF3B30] focus:outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-widest py-4 hover:bg-[#e02e24] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Accesso..." : "Entra"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <div className="mt-6 text-sm text-[#A0A0AB]">
            Nuovo qui?{" "}
            <Link to="/register" data-testid="link-register" className="text-white hover:text-[#FF3B30] font-bold uppercase tracking-widest text-xs">
              Registrati →
            </Link>
          </div>
          <div className="mt-2 text-xs text-[#A0A0AB]">
            Password dimenticata?
            <button
              type="button"
              onClick={forgotPassword}
              disabled={forgotLoading}
              data-testid="btn-forgot-email"
              className="ml-2 text-[#E2FF31] hover:underline uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Mail className="w-3 h-3" /> {forgotLoading ? "Invio..." : "Ricevi email di reset"}
            </button>
          </div>
          <div className="mt-2 text-xs text-[#A0A0AB]">
            Hai già un codice?{" "}
            <Link to="/reset-password" data-testid="link-forgot" className="text-white hover:text-[#FF3B30] uppercase tracking-widest">
              Vai al reset →
            </Link>
          </div>
          <div className="mt-1 text-[10px] text-[#A0A0AB] leading-relaxed">
            Non ricevi l'email? Contatta il tuo coach: potrà generare manualmente un codice dalla console.
          </div>
        </div>
      </div>
    </div>
  );
}
